import { createAction } from 'redux-actions';
import * as actionType from './actionTypes';
import client from '../../apollo';
import externalProductCatalogQuery from '../../graphql/externalProductCatalogQuery';
import guaranteeForceOrdersQuery from '../../graphql/guaranteeForceOrdersQuery';
import ruleEvaluationOutcomesQuery from '../../graphql/ruleEvaluationOutcomesQuery';
import postGuaranteeForceMutation from '../../graphql/postGuaranteeForceMutation';
import {
  toTransaction,
  toRuleOutcome,
  toForceResult,
} from '../../utilities/forceGuarantee';

export const setProducts = createAction(actionType.SET_PRODUCTS);
export const setProductsLoading = createAction(actionType.SET_PRODUCTS_LOADING);
export const setProductsError = createAction(actionType.SET_PRODUCTS_ERROR);
export const setSearch = createAction(actionType.SET_SEARCH);
export const setTransactions = createAction(actionType.SET_TRANSACTIONS);
export const setTransactionsLoading = createAction(
  actionType.SET_TRANSACTIONS_LOADING
);
export const setTransactionsError = createAction(
  actionType.SET_TRANSACTIONS_ERROR
);
export const setSelectedTransaction = createAction(
  actionType.SET_SELECTED_TRANSACTION
);
export const setEvaluation = createAction(actionType.SET_EVALUATION);
export const setEvaluationLoading = createAction(
  actionType.SET_EVALUATION_LOADING
);
export const setEvaluationError = createAction(actionType.SET_EVALUATION_ERROR);
export const clearEvaluation = createAction(actionType.CLEAR_EVALUATION);
export const setForceResult = createAction(actionType.SET_FORCE_RESULT);
export const setForceLoading = createAction(actionType.SET_FORCE_LOADING);
export const setForceError = createAction(actionType.SET_FORCE_ERROR);
export const setModalOpen = createAction(actionType.SET_MODAL_OPEN);
export const resetFlowAction = createAction(actionType.RESET);

// Monotonically increasing counters used to discard stale responses when the
// user starts a new lookup / eligibility run before the previous one settles.
let ordersRequestCounter = 0;
let evaluationRequestCounter = 0;
let forceRequestCounter = 0;

// The app has no shared logger, and the store only keeps an error until the
// next reset — without this the original failure is lost and support has
// nothing to correlate with the backend. Identifiers only: the VIN and the
// justification text stay out of the console.
const logFailure = (operation, context, err) => {
  // eslint-disable-next-line no-console
  console.error(`forceGuarantee: ${operation} failed`, context, err);
};

// Leaving the page (or starting over) does not cancel the in-flight requests,
// so the counters have to be bumped here as well — otherwise a late response
// lands in the store that was just cleared and the next visit shows results
// from the previous session.
export const reset = () => dispatch => {
  ordersRequestCounter += 1;
  evaluationRequestCounter += 1;
  forceRequestCounter += 1;
  dispatch(resetFlowAction());
};

// Loaded once, on page mount, to populate the product Dropdown.
export const fetchProducts = () => async dispatch => {
  dispatch(setProductsLoading(true));
  try {
    const { data } = await client.query({
      query: externalProductCatalogQuery,
      fetchPolicy: 'network-only',
    });
    dispatch(setProducts((data && data.externalProductCatalog) || []));
  } catch (err) {
    logFailure('fetchProducts', {}, err);
    dispatch(setProductsError(err));
  }
};

// Fetches the INELIGIBLE orders available for force-guarantee for the selected
// product + VIN.
export const fetchForceData = (productCode, vin) => async dispatch => {
  ordersRequestCounter += 1;
  const requestId = ordersRequestCounter;
  dispatch(setSearch({ productCode, vin }));
  dispatch(setTransactionsLoading(true));
  try {
    const { data } = await client.query({
      query: guaranteeForceOrdersQuery,
      variables: { productCode, vin },
      fetchPolicy: 'network-only',
    });
    if (requestId !== ordersRequestCounter) return;
    const orders = (data && data.guaranteeForceOrders) || [];
    dispatch(setTransactions(orders.map(toTransaction)));
  } catch (err) {
    logFailure('guaranteeForceOrders', { productCode }, err);
    if (requestId === ordersRequestCounter) {
      dispatch(setTransactionsError(err));
    }
  }
};

// Runs the full ruleset: does NOT stop on first failure, the backend returns
// every rule outcome.
export const runEligibilityCheck = orderId => async dispatch => {
  if (!orderId) return;
  evaluationRequestCounter += 1;
  const requestId = evaluationRequestCounter;
  dispatch(setEvaluationLoading(true));
  try {
    const { data } = await client.query({
      query: ruleEvaluationOutcomesQuery,
      variables: { orderId: String(orderId) },
      fetchPolicy: 'network-only',
    });
    if (requestId !== evaluationRequestCounter) return;
    const outcomes = (data && data.ruleEvaluationOutcomes) || [];
    dispatch(setEvaluation({ orderId, results: outcomes.map(toRuleOutcome) }));
  } catch (err) {
    logFailure('ruleEvaluationOutcomes', { orderId }, err);
    if (requestId === evaluationRequestCounter) {
      dispatch(setEvaluationError(err));
    }
  }
};

export const forceGuarantee = ({ transactionId, justification } = {}) => async dispatch => {
  if (!transactionId) return null;
  forceRequestCounter += 1;
  const requestId = forceRequestCounter;
  dispatch(setForceLoading(true));
  try {
    const { data } = await client.mutate({
      mutation: postGuaranteeForceMutation,
      variables: {
        input: {
          orderId: transactionId,
          justification,
        },
      },
    });
    const result = toForceResult(data && data.postGuaranteeForce);
    // The guarantee is already created on the backend at this point; only the
    // store update is dropped, the operation itself stays in the APMS audit log.
    if (requestId !== forceRequestCounter) return null;
    dispatch(setForceResult(result));
    return result;
  } catch (err) {
    logFailure('postGuaranteeForce', { orderId: transactionId }, err);
    if (requestId === forceRequestCounter) {
      dispatch(setForceError(err));
    }
    return null;
  }
};
